import {
  EXPERIMENT_STEPS,
  describeDeltaE,
  labToSrgb,
  type CompletionSummary,
  type ExperimentStepKey,
} from '@dyeatlas/shared'
import { AlertTriangle, Check } from 'lucide-react'
import { STEP_FIELDS, type FieldDef } from './fields'
import { Swatch } from '@/components/ui/Badge'
import { Notice } from '@/components/ui/misc'
import type { useMasterData } from '@/hooks/useMasterData'
import { cn, emptyDash, formatDate } from '@/lib/utils'

/**
 * The final step reads back everything that was entered, grouped by the step it
 * came from, with the derived swatch alongside. Clicking a group jumps back to it.
 */
export function ReviewStep({
  values,
  completion,
  options,
  onJumpToStep,
}: {
  values: Record<string, unknown>
  completion: CompletionSummary
  options: ReturnType<typeof useMasterData>['options']
  onJumpToStep: (step: ExperimentStepKey) => void
}) {
  const lab = labValues(values)
  const swatch = lab ? labToSrgb(lab) : null

  return (
    <div className="space-y-6">
      {completion.canFinish ? (
        <Notice tone="positive" title="Ready to finish" icon={<Check className="size-4" />}>
          Every required field is filled in. Finishing assigns a permanent record number and makes
          this experiment available to submit for peer review.
        </Notice>
      ) : (
        <Notice
          tone="caution"
          title={`${completion.missingRequired.length} required field${completion.missingRequired.length === 1 ? '' : 's'} still empty`}
          icon={<AlertTriangle className="size-4" />}
        >
          You can keep this as a draft, but it cannot be finished until{' '}
          {completion.incompleteSteps
            .map((step) => EXPERIMENT_STEPS.find((entry) => entry.key === step)?.title)
            .filter(Boolean)
            .join(', ')}{' '}
          {completion.incompleteSteps.length === 1 ? 'is' : 'are'} complete.
        </Notice>
      )}

      {swatch && (
        <div className="flex items-center gap-4 rounded-[var(--radius-card)] border border-line bg-paper-muted p-4">
          <Swatch hex={swatch.hex} size="lg" className="size-16 rounded-[var(--radius-control)]" />
          <div className="text-sm">
            <p className="font-display text-lg font-semibold text-ink">
              L* {lab!.L} · a* {lab!.a} · b* {lab!.b}
            </p>
            <p className="text-[12px] text-ink-muted">
              <span className="font-mono">{swatch.hex}</span>
              {swatch.outOfGamut && (
                <span className="text-caution"> (outside sRGB: nearest displayable colour)</span>
              )}
            </p>
          </div>
          <p className="ml-auto max-w-[220px] text-right text-[12px] leading-relaxed text-ink-faint">
            Colour differences in DyeAtlas are reported as ΔE₀₀, where{' '}
            {describeDeltaE(1).toLowerCase()} at ΔE₀₀ 1.
          </p>
        </div>
      )}

      {EXPERIMENT_STEPS.filter((step) => step.schema !== null).map((step) => (
        <section key={step.key}>
          <header className="mb-2 flex items-baseline justify-between gap-3">
            <h3 className="font-display text-base font-semibold text-ink">{step.title}</h3>
            <button
              type="button"
              onClick={() => onJumpToStep(step.key)}
              className="text-[12px] font-semibold text-brand hover:underline"
            >
              Edit
            </button>
          </header>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-[var(--radius-control)] border border-line bg-paper p-4 text-sm">
            {STEP_FIELDS[step.key as keyof typeof STEP_FIELDS].map((field) => (
              <div key={field.name} className={cn('flex justify-between gap-4', field.wide && 'col-span-2')}>
                <dt className="text-ink-muted">{field.label}</dt>
                <dd
                  className={cn(
                    'text-right text-ink',
                    field.required && !hasValue(values[field.name]) && 'text-brand',
                  )}
                >
                  {display(field, values[field.name], options)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  )
}

function labValues(values: Record<string, unknown>) {
  const L = Number(values.resLabL)
  const a = Number(values.resLabA)
  const b = Number(values.resLabB)
  if (![L, a, b].every(Number.isFinite)) return null
  return { L, a, b }
}

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)

/** Resolves vocabulary ids back to the names the researcher chose. */
function display(
  field: FieldDef,
  value: unknown,
  options: ReturnType<typeof useMasterData>['options'],
): string {
  if (!hasValue(value)) return field.required ? 'Required' : '—'

  if (field.vocabulary) {
    const list = options[field.vocabulary]
    if (Array.isArray(value)) {
      return value
        .map((id) => list.find((option) => option.value === id)?.label ?? '—')
        .join(', ')
    }
    return list.find((option) => option.value === value)?.label ?? '—'
  }

  if (field.kind === 'date') return formatDate(String(value))
  return `${emptyDash(value as string)}${field.suffix ? ` ${field.suffix}` : ''}`
}
